import { useMemo } from 'react';

import { useShallow } from 'zustand/react/shallow';
import { useTranslation } from 'react-i18next';
import { ArrowLeftCircle, ArrowRightCircle } from 'lucide-react';

import { getPatientInfoFromPatientId } from '@/utils/cases';
import { useGradeFlow } from '@/states/grade-flow';

import { RadioGroupOption } from '@/components/form/radio-group-opt';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/form/label';

type LeftSideDataProps = {
  img: string;
  onCloseModal: () => void;
  setExpanded: (img: string) => void;
};

export function LeftSideData(props: LeftSideDataProps) {
  const { onCloseModal, setExpanded, img } = props;
  const { t } = useTranslation();

  const { readableImages, setImageReadable, isSaving, caseData } = useGradeFlow(
    useShallow((s) => ({
      isSaving: s.isSavingGrade,
      readableImages: s.form.images_readable ?? [],
      setImageReadable: s.setImageReadable,
      caseData: s.case,
    }))
  );

  const currentIndex = readableImages.findIndex((state) => state.img === img);
  const currentImage = readableImages[currentIndex ?? -1];
  const totalImages = readableImages.length ?? 0;

  const handleMoveLeft = () => {
    if (currentIndex !== undefined && currentIndex > 0) {
      const previousImage = readableImages[currentIndex - 1];
      if (previousImage) {
        setExpanded(previousImage.img);
      }
    }
  };

  const handleMoveRight = () => {
    if (currentIndex !== undefined && currentIndex < totalImages) {
      const nextImage = readableImages[currentIndex + 1];
      if (nextImage) {
        setExpanded(nextImage.img);
      }
    }
  };

  const patient = useMemo(() => {
    if (!caseData) return undefined;

    const patientData = getPatientInfoFromPatientId(caseData.patient_id);
    return patientData;
  }, [caseData]);

  const validateAutoCloseModal = () => {
    if (!readableImages) return;

    const alreadyAnswered = readableImages.filter((state) => state.is_readable !== '');
    const isLastPending = alreadyAnswered.length + 1 >= readableImages.length; // +1 because this image is also included

    // if it was the last image pending to be answered, close the modal
    if (isLastPending) onCloseModal();
  };

  if (!currentImage) return null;

  return (
    <div className="rounded-lg bg-white bg-opacity-90 p-4 inline-flex flex-col shrink-0 w-full lg:w-[48%] xl:w-52 xl:order-1 order-3">
      {patient ? (
        <>
          <h3 className="text-base/7 font-semibold text-gray-900">{t('patient-info')}</h3>
          <p className="text-sm text-gray-500">
            {t('name-label')}: {patient?.patientName}
          </p>
          <p className="text-sm text-gray-500">
            {t('age-label')}: {patient?.age}
          </p>
        </>
      ) : null}

      <hr className="my-4" />
      <Label className="mb-2">{t('grade-image-readable-question')}</Label>
      <div className="flex gap-4">
        <RadioGroupOption
          label={t('yes')}
          id={`${currentImage.img}-readable-yes`}
          value="yes"
          required
          disabled={isSaving}
          checked={currentImage.is_readable === 'yes'}
          onChange={() => {
            setImageReadable(currentImage.img, true);
            validateAutoCloseModal();
          }}
        />
        <RadioGroupOption
          label={t('no')}
          id={`${currentImage.img}-readable-no`}
          value="no"
          required
          disabled={isSaving}
          checked={currentImage.is_readable === 'no'}
          onChange={() => {
            setImageReadable(currentImage.img, false);
            validateAutoCloseModal();
          }}
        />
      </div>

      <hr className="my-4" />
      <div className="flex justify-between items-center mt-2">
        <Button
          onClick={handleMoveLeft}
          disabled={currentIndex === 0}
          variant="ghost-secondary"
          size="icon"
        >
          <ArrowLeftCircle className="size-6" />
        </Button>
        <span className="text-xs">
          {(currentIndex ?? 0) + 1} {totalImages ? `of ${totalImages}` : ''}
        </span>
        <Button
          onClick={handleMoveRight}
          disabled={!totalImages || currentIndex + 1 === totalImages}
          variant="ghost-secondary"
          size="icon"
        >
          <ArrowRightCircle className="size-6" />
        </Button>
      </div>
    </div>
  );
}
